import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  IconButton,
  Card,
  Chip,
  CardContent,
} from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import configServ from "../../services/config";
import { useSelector } from "react-redux";
import { Edit, RemoveRedEyeOutlined } from "@mui/icons-material";
// import KuldeviPhotoPop from "./KuldeviPhotoPop";

const DailyUrlTable = ({ handleEdit, refreshKey }) => {
  const [DailyList, setDailyList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const { company_id } = useSelector((state) => state.GlobalVariables);

  // Filters & sorting
  const [searchTerm, setSearchTerm] = useState("");
  const [searchType, setSearchType] = useState("title");
  const [sortField, setSortField] = useState("daily_updates.id");
  const [sortOrder, setSortOrder] = useState("desc");

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const headers = [
    { label: "S.No.", value: "daily_updates.id" },
    { label: "Title", value: "title" },
    { label: "Auther Name ", value: "author_name" },
    { label: "Category", value: "categoryname" },
    { label: "Daily Url", value: "daily_url" },
    { label: "Status", value: "kd.active" },
    { label: "Action", value: "action" },
  ];

  const headerCellStyle = {
    fontSize: 14,
    fontWeight: "bold",
    backgroundColor: "#0c79e6ff",
    color: "white",
    padding: 8,
    textAlign: "center",
    cursor: "pointer",
  };
  const cellStyle = { py: 0.5, textAlign: "center" };

  // ✅ Fetch data dynamically from backend
  const fetchDailyUpdates = async () => {
    try {
      setLoading(true);
      const dataToSend = {
        company_id,
        page: page + 1,
        limit: rowsPerPage,
        search_field: searchType,
        search_value: searchTerm.trim(),
        sort_field: sortField,
        sort_order: sortOrder,
      };

      const result = await configServ.getAllDailyUpdates(dataToSend);
      if (result?.success === true) {
        setDailyList(result.data || []);
        setTotal(result.total || 0);
      } else {
        setDailyList([]);
        setTotal(0);
      }
    } catch (error) {
      console.error("Error fetching Kuldevi details:", error);
      setDailyList([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  // 🚀 Re-fetch whenever filter/sort/pagination changes
  useEffect(() => {
    if (company_id) fetchDailyUpdates();
  }, [
    company_id,
    page,
    rowsPerPage,
    searchTerm,
    searchType,
    sortField,
    sortOrder,
    refreshKey,
  ]);

  // Pagination handlers
  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // 🔁 Sorting toggle on header click
  const handleSort = (field) => {
    if (field === "kd.active" || field === "kd.id") return; // ignore action column

    if (sortField === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  // Reset filters
  const handleReset = () => {
    setSearchTerm("");
    setSearchType("title");
    setSortField("daily_updates.id");
    setSortOrder("desc");
    setPage(0);
  };

  return (
    <>
      {/* <KuldeviPhotoPop
        open={openImagePop}
        onClose={() => setOpenImagePop(false)}
        productId={selectedProductId}
        fetchImages={configServ.getKuldeviPhotos}
      /> */}
      {/* 🔹 Filters Section */}
      <Box
        sx={{
          mt: 2,
          mb: 3,
          p: 2,
          backgroundColor: "#fff",
          borderRadius: 2,
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          width: "100%",
        }}
      >
        <Grid
          container
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
        >
          {/* Total */}
          <Grid item xs={12} sm="auto">
            <Typography variant="subtitle1" fontWeight="bold">
              Total: <span style={{ color: "#1976d2" }}>{total}</span>
            </Typography>
          </Grid>

          {/* Search & Filter */}
          <Grid
            item
            xs={12}
            sm
            container
            spacing={1}
            alignItems="center"
            justifyContent={{ xs: "flex-start", sm: "flex-end" }}
          >
            {/* Search By */}
            <Grid item xs={12} sm="auto">
              <FormControl size="small" fullWidth>
                <InputLabel>Search By</InputLabel>
                <Select
                  value={searchType}
                  onChange={(e) => setSearchType(e.target.value)}
                  label="Search By"
                >
                  <MenuItem value="title">Title</MenuItem>
                  <MenuItem value="categoryname">Category</MenuItem>
                  <MenuItem value="author_name">Author Name</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Search Input */}
            <Grid item xs={12} sm="auto">
              <TextField
                size="small"
                fullWidth
                label="Search"
                variant="outlined"
                value={searchTerm}
                onChange={(e) => {
                  setPage(0);
                  setSearchTerm(e.target.value);
                }}
              />
            </Grid>

            {/* Reset Button */}
            <Grid item xs={12} sm="auto">
              <Button
                variant="outlined"
                color="secondary"
                size="small"
                startIcon={<RestartAltIcon />}
                onClick={handleReset}
                fullWidth
              >
                Reset
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Box>

      {/* 🔹 Table Section */}
      <TableContainer
        component={Paper}
        sx={{ display: { xs: "none", md: "block" } }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {headers.map((header) => {
                const isActive = sortField === header.value;
                return (
                  <TableCell
                    key={header.value}
                    style={headerCellStyle}
                    onClick={() => handleSort(header.value)}
                    sx={{
                      cursor: ![
                        "kd.active",
                        "daily_updates.id",
                        "action",
                      ].includes(header.value)
                        ? "pointer"
                        : "default",
                    }}
                  >
                    {header.label}
                    {!["kd.active", "daily_updates.id", "action"].includes(
                      header.value
                    ) && (
                      <span style={{ marginLeft: 4 }}>
                        {isActive ? (sortOrder === "asc" ? "▲" : "▼") : "⇅"}
                      </span>
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={headers.length} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : DailyList.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={headers.length}
                  align="center"
                  sx={{ color: "red" }}
                >
                  <b>No Data Found</b>
                </TableCell>
              </TableRow>
            ) : (
              DailyList.map((row, rowIndex) => (
                <TableRow
                  key={row.id}
                  sx={{ "&:hover": { backgroundColor: "#f5faff" } }}
                >
                  {/* <TableCell sx={cellStyle}>{row.id}</TableCell> */}
                  <TableCell sx={cellStyle}>
                    {page * rowsPerPage + rowIndex + 1}
                  </TableCell>
                  <TableCell sx={cellStyle}>{row.title || "—"}</TableCell>

                  <TableCell sx={cellStyle}>{row.author_name || "—"}</TableCell>
                  <TableCell sx={cellStyle}>
                    {row.categoryname || "—"}
                  </TableCell>
                  <TableCell sx={cellStyle}>{row.daily_url || "—"}</TableCell>

                  {/* <TableCell sx={cellStyle}>
                    {row.active === 1 ? "Active" : "Inactive"}
                  </TableCell> */}
                  <TableCell>
                    <Chip
                      label={row.active ? "Active" : "Inactive"}
                      color={row.active ? "success" : "default"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell
                    sx={{
                      ...cellStyle,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    {/* 🗑️ Delete Button */}
                    {/* <IconButton
                          color="error"
                          size="small"
                          onClick={() => handleDelete(row)}
                        >
                          <GridDeleteIcon fontSize="small" />
                        </IconButton> */}

                    <IconButton
                      color="primary"
                      size="small"
                      onClick={() => handleEdit(row)}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {/* Backend Pagination */}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      {/* 🔹 Mobile View */}
      <Box sx={{ display: { xs: "block", md: "none" }, mt: 2 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" py={5}>
            <CircularProgress />
          </Box>
        ) : DailyList.length === 0 ? (
          <Typography align="center" color="red">
            <b>No Data Found</b>
          </Typography>
        ) : (
          DailyList.map((row, index) => (
            <Card
              key={row.id}
              sx={{
                mb: 2,
                p: 2,
                boxShadow: 2,
                borderRadius: 2,
                position: "relative",
              }}
            >
              {/* Serial Number */}
              <Typography variant="caption" color="textSecondary">
                S.NO{page * rowsPerPage + index + 1}
              </Typography>

              {/* Title */}
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                 <strong>Author:</strong> {row.title || "—"}
              </Typography>

              {/* Author */}
              <Typography variant="body2">
                <strong>Author:</strong> {row.author_name || "—"}
              </Typography>

              {/* Category */}
              <Typography variant="body2">
                <strong>Category:</strong> {row.categoryname || "—"}
              </Typography>

              {/* Daily URL */}
              <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                <strong>Daily URL:</strong>{" "}
                {row.daily_url ? (
                  <a
                    href={row.daily_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {row.daily_url}
                  </a>
                ) : (
                  "—"
                )}
              </Typography>

              {/* Status */}
              <Box mt={1}>
                <Chip
                  label={row.active ? "Active" : "Inactive"}
                  color={row.active ? "success" : "default"}
                  size="small"
                />
              </Box>

              {/* Action Buttons */}
              <Box mt={1} display="flex" gap={1}>
                <IconButton
                  color="primary"
                  size="small"
                  onClick={() => handleEdit(row)}
                >
                  <Edit fontSize="small" />
                </IconButton>
                {/* Add delete or other buttons if needed */}
              </Box>
            </Card>
          ))
        )}

        {/* Mobile Pagination */}
        {DailyList.length > 0 && (
          <Box display="flex" justifyContent="center" mt={2}>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={total}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                "& .MuiTablePagination-toolbar": {
                  flexWrap: "wrap",
                  justifyContent: "center",
                },
              }}
            />
          </Box>
        )}
      </Box>
    </>
  );
};

export default DailyUrlTable;
